import { useState } from 'react'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, ArrowLeft, Users, CheckCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Badge } from '@/clientWeb/components/ui/badge'
import { NoData } from '@/clientWeb/components/ui/no-data'
import nodataAnalyticsImg from '@/assets/nodataanalytics.svg'

interface PollResult {
  id: string
  question: string
  meeting: string
  votes: number
  options: { label: string; votes: number }[]
}

// No analytics API yet (see services/analytics.repo.ts), so there are no poll
// results. This used to show three invented polls from made-up meetings.
const POLLS: PollResult[] = []

function PollCard({ poll }: { poll: PollResult }) {
  const t = useTranslation()
  const [open, setOpen] = useState(false)
  const max = Math.max(...poll.options.map(o => o.votes))

  return (
    <motion.div
      layout
      className="bg-card border border-border rounded-2xl overflow-hidden"
    >
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-start justify-between p-4 text-left gap-3 hover:bg-muted/30 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground mb-1 leading-snug">{poll.question}</p>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="text-[10px]">{poll.meeting}</Badge>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Users className="w-3 h-3" />{poll.votes} {t("analytics.votes")}
            </span>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-border"
          >
            <div className="p-4 space-y-3">
              {poll.options.map(opt => {
                const pct = Math.round((opt.votes / poll.votes) * 100)
                const isTop = opt.votes === max
                return (
                  <div key={opt.label} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className={`font-medium ${isTop ? 'text-primary' : 'text-foreground'}`}>
                        {isTop && <CheckCircle className="w-3 h-3 inline mr-1 text-primary" />}
                        {opt.label}
                      </span>
                      <span className="text-muted-foreground tabular-nums">{opt.votes} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
                        className={`h-full rounded-full ${isTop ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                      />
                    </div>
                  </div>
                )
              })}
              <div className="pt-2 border-t border-border/50">
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={poll.options} barSize={28}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} />
                    <YAxis tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} />
                    <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 11 }} labelStyle={{ color: 'var(--foreground)' }} itemStyle={{ color: 'inherit' }} wrapperClassName="text-primary" />
                    <Bar dataKey="votes" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default function PollsAnalyticsPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-4 sm:px-6 py-3 border-b border-border bg-card shrink-0">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          <button onClick={() => navigate('/analytics')} className="hover:text-foreground flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-3 h-3" />{t("analytics.title")}
          </button>
          <span>/</span><span className="text-foreground">{t("analytics.polls")}</span>
        </div>
        <h1 className="text-lg font-bold text-foreground">{t("analytics.pollResults")}</h1>
      </div>
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
        {POLLS.length === 0 && (
          <NoData image={nodataAnalyticsImg} title={t("analytics.noDataAvailable")} size="md" showActionIcon={false} />
        )}
        {POLLS.map((poll, i) => (
          <motion.div key={poll.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
            <PollCard poll={poll} />
          </motion.div>
        ))}
      </div>
    </div>
  )
}
